import { FC, Suspense } from "react";
import { AppProps } from "next/app";
import Head from "next/head";
import { Provider } from "jotai";

// Pinch-zoom stays available on the website (WCAG 1.4.4; Lighthouse fails `user-scalable=no` and a
// maximum-scale below 5). Only the native app shell, whose web view reports this user agent
// (capacitor.config.ts `overrideUserAgent`), keeps the page at a fixed scale like a native screen.
// The head is not hydrated, so the server's website value is simply replaced in the app.
const VIEWPORT = "width=device-width, initial-scale=1, viewport-fit=cover";
const APP_VIEWPORT = "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";
const isAppShell = () => typeof navigator !== "undefined" && navigator.userAgent === "Ortex App";

export const withApp = (Component: FC<AppProps>) =>
    function WithApp(props: AppProps) {
        return (
            <>
                <Head>
                    <meta name="viewport" content={isAppShell() ? APP_VIEWPORT : VIEWPORT} />
                </Head>
                {/* One hydration boundary around the whole app. React 18 hydrates whatever sits inside a
                    boundary in a later, idle pass, so the app's start-up effects (user, settings, universe)
                    must be inside it with the page: then everything hydrates in one pass and those effects
                    run after it. With the page in a boundary of its own, they ran first and the page
                    hydrated against changed state, a mismatch on any page that reads it. The boundary also
                    keeps a mismatch contained: without one the root recovered by retrying hydration over
                    and over (a frozen tab). Nothing above it changes, so it never sees an update while it
                    hydrates (React #421). */}
                <Suspense fallback={null}>
                    <Provider>
                        <Component {...props} />
                    </Provider>
                </Suspense>
            </>
        );
    };
